import { WorkerStage } from "@clipboard-health/contract-worker-app-bff";
import { isDefined } from "@clipboard-health/util-ts";
import { useGetQuery, type UseGetQueryOptions } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { type UseQueryResult } from "@tanstack/react-query";
import { minutesToMilliseconds } from "date-fns";
import { z } from "zod";

export const latenessConfigEntrySchema = z.object({
  points: z.number(),
  minutesLateRange: z.object({
    startFrom: z.number(),
    stopAt: z.number().optional(),
  }),
  pointsWithEmergencyCredit: z.number().optional(),
});

export type LatenessConfigEntry = z.infer<typeof latenessConfigEntrySchema>;

const latenessConfigSchema = z.array(latenessConfigEntrySchema);

const clipboardScorePenaltyMultipliersSchema = z.object({
  cancellation: z.object({
    next: z.number(),
  }),
  lateness: z.object({
    next: z.number(),
  }),
});

const cancelShiftSchema = z.object({
  points: z.number(),
  leadTime: z.array(z.number().nullable()),
  pointsWithEmergencyCredit: z.number().optional(),
});

export const clipboardScorePolicySchema = z
  .object({
    workShift: z
      .object({
        points: z.number(),
      })
      .optional(),
    qualityBonusConfig: z
      .object({
        points: z.number(),
      })
      .optional(),
    suspension: z
      .array(
        z.object({
          score: z.tuple([z.number().nullable(), z.number().nullable()]),
          duration: z.number(),
        })
      )
      .optional(),
    cancelShift: z.array(cancelShiftSchema).optional(),
    latenessConfig: latenessConfigSchema.optional(),
    shiftClockInConfig: z
      .object({
        points: z.number(),
        requiredClockInMinutesBeforeShift: z.number().optional(),
      })
      .optional(),
    deactivation: z
      .object({
        window: z.number(),
        duration: z.number(),
        suspension: z.number(),
      })
      .optional(),
    noCallNoShow: z
      .object({
        points: z.number(),
      })
      .optional(),
    finalRestriction: z
      .object({
        window: z.number(),
        duration: z.number(),
        restriction: z.number(),
      })
      .optional(),
    restriction: z
      .array(
        z.object({
          score: z.tuple([z.number().nullable(), z.number().nullable()]),
          duration: z.number(),
        })
      )
      .optional(),
  })
  .optional();

const restrictionSchema = z.object({
  nextDuration: z.number().optional(),
  reEnrollmentScore: z.number().optional(),
});

const scoreFreezeReasonSchema = z.enum(["WORKER_STATUS"]);

const scoreFreezeSchema = z.discriminatedUnion("enabled", [
  z.object({
    enabled: z.literal(false),
  }),
  z.object({
    enabled: z.literal(true),
    reason: scoreFreezeReasonSchema,
  }),
]);

const emergencyCreditStatsCommonSchema = z.object({
  totalAvailableCredits: z.number(),
  lastCreditUsedOn: z.string().nullable(),
  completedShiftsTowardsNextCredit: z.number(),
  requiredShiftsForNextCredit: z.number(),
  creditCap: z.number(),
});

const emergencyCreditStatsSchema = z.object({
  lateness: emergencyCreditStatsCommonSchema,
  cancellation: emergencyCreditStatsCommonSchema,
});

export type EmergencyCreditStatsCommon = z.infer<typeof emergencyCreditStatsCommonSchema>;
export type EmergencyCreditStats = z.infer<typeof emergencyCreditStatsSchema>;

export const clipboardScoreProfileResponseSchema = z.object({
  clipboardScore: z.number(),
  clipboardScoreRank: z
    .object({
      rank: z.number(),
      outOf: z.number(),
      qualification: z.string(),
    })
    .optional(),
  suspensionCount: z.number().optional(),
  latenessPointsDeductedInLast30Days: z.number().optional(),
  latenessBooktimeThreshold: z.number().optional(),
  accountStatus: z.object({
    status: z
      .enum([
        WorkerStage.ONBOARDING,
        WorkerStage.ENROLLED,
        WorkerStage.PROBATION,
        WorkerStage.DEACTIVATED,
        WorkerStage.RESTRICTED,
      ])
      .nullish(),
    reEnrollmentDate: z.string().optional(),
    restrictionDuration: z.number().optional(),
  }),
  policy: clipboardScorePolicySchema,
  restrictionCount: z.number().optional(),
  clipboardScorePenaltyMultipliers: clipboardScorePenaltyMultipliersSchema.optional(),
  restriction: restrictionSchema.optional(),
  scoreFreeze: scoreFreezeSchema,
  emergencyCreditStats: emergencyCreditStatsSchema.optional(),
});

export type ClipboardScorePenaltyMultipliers = z.infer<
  typeof clipboardScorePenaltyMultipliersSchema
>;
export type ClipboardScorePolicy = z.infer<typeof clipboardScorePolicySchema>;
export type ClipboardScoreProfileResponse = z.infer<typeof clipboardScoreProfileResponseSchema>;
export type CancelShiftPolicy = z.infer<typeof cancelShiftSchema>;
export type FinalRestriction = NonNullable<
  ClipboardScoreProfileResponse["policy"]
>["finalRestriction"];
export type LatenessConfigResponse = z.infer<typeof latenessConfigSchema>;
export type ScoreFreezeReason = z.infer<typeof scoreFreezeReasonSchema>;

export function getClipboardScoreProfilePath(userId: string): string {
  return `/workers/${userId}/profile`;
}

export function getClipboardScoreProfileUrl(userId: string): string {
  return `${environmentConfig.REACT_APP_ATTENDANCE_SCORE_SERVICE_URL}${getClipboardScoreProfilePath(userId)}`;
}

export function useGetClipboardScoreProfile(
  userId: string | undefined,
  options: UseGetQueryOptions<ClipboardScoreProfileResponse> = {},
  qualification?: string,
  workplaceId?: string
): UseQueryResult<ClipboardScoreProfileResponse> {
  const url = getClipboardScoreProfileUrl(userId ?? "");

  const qualificationParams = isDefined(qualification) ? { qualification } : {};
  const workplaceIdParams = isDefined(workplaceId) ? { workplaceId } : {};

  const queryParams = { ...qualificationParams, ...workplaceIdParams };

  return useGetQuery({
    url,
    responseSchema: clipboardScoreProfileResponseSchema,
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.GET_ATTENDANCE_SCORE_PROFILE,
      userErrorMessage: "Something went wrong while loading your clipboard score.",
    },
    enabled: Boolean(userId),
    queryParams,
    staleTime: minutesToMilliseconds(5),
    ...options,
  });
}
